// content/bosses/boss3.ts — BOSS 3: THE TWICE-CROWNED (DESIGN §5, Zone 3).
//
//   "Verb: seal the kill — the fight's own death must be denied."
//
// The last king of Hollowdeep, fought in his own throne hall with the empty
// throne looming upstage. He is a duelist, not a wall of meat: he closes with
// poise, strikes in telegraphed bursts behind a red glint, blinks through your
// answer on a cadence you can learn, and keeps a pair of Courtiers on the floor
// so the zone's own rule — seal it or it gets back up — never leaves your plate.
//
// THE HINGE is the fight. At 0% he collapses and his Crown scuttles off his
// body toward the throne on spider-legs: six seconds, sixty hit points,
// staggerable. Let it sit down and he re-forms at FULL health, enraged. Break
// it and he still rises — the Gloom drags him up — but at 55% and unhurried.
// Either way there is a second fight; the hinge decides how big it is.
//
// Every literal comes from data/zones/zone3.ts (ZONE3_BOSS). The only numbers
// written here are geometry constants of the drawing.

import type { Draw, EliteRider, Enemy, RunCtx, Vec } from '../../types'
import { Boss, registerBoss } from '../../game/boss'
import { bossDefeated, registerBossArena, registerZoneContent } from '../../game/zones'
import { moveEnemy, registerEnemyBrain } from '../../game/enemies'
import { TILE } from '../../game/world'
import {
  ZONE3_BOSS,
  ZONE3_BOSS_ARENA,
  ZONE3_BOSS_ENEMIES,
} from '../../data/zones/zone3'
import { emit } from '../../engine/particles'
import { PixelArt, drawSprite, registerPainter, registerRecipe, shade } from '../../engine/sprites'
import type { EnemyEnt } from '../../game/entities'
import type { Run } from '../../game/game'

const D = ZONE3_BOSS
const TAU = Math.PI * 2

const tileCenter = (t: { tx: number; ty: number }): Vec => ({
  x: t.tx * TILE + TILE / 2,
  y: t.ty * TILE + TILE / 2,
})

/** Per-phase array read, clamped — phases are 1-based. */
function byPhase<T>(arr: readonly T[], phase: number): T {
  return arr[Math.max(0, Math.min(arr.length - 1, phase - 1))]
}

function num(v: unknown, d: number): number {
  return typeof v === 'number' ? v : d
}

const posScratch: Vec = { x: 0, y: 0 }

// ===========================================================================
// The Crown — a body, so that everything already knows how to fight it.
//
// It is an ordinary enemy with a custom brain: every weapon hits it, the Maul
// staggers it (game/enemies.ts applies tags.stunT before any custom brain runs,
// so a stagger really does stop the crawl), splash and the potion catch it, and
// Snapshot counts it. Its HP is written by the boss at spawn so the zone's stat
// scale can never drift the hinge off DESIGN's 60.
// ===========================================================================

registerEnemyBrain('z3-crown', (run: Run, e: EnemyEnt, dt: number): void => {
  const tx = num(e.tags.tx, e.pos.x)
  const ty = num(e.tags.ty, e.pos.y)
  const dx = tx - e.pos.x
  const dy = ty - e.pos.y
  const d = Math.hypot(dx, dy) || 1
  // deliberately NOT multiplied by the Gloom's speed mul: the hinge window is a
  // fixed promise to the player, and the Gloom is paused in a boss arena anyway.
  moveEnemy(run, e, (dx / d) * e.speed, (dy / d) * e.speed, dt)
  e.facing = dx < 0 ? -1 : 1
  e.bob += dt * 2
  if (e.bob % 0.12 < dt) {
    emit(e.pos.x, e.pos.y + 6, { count: 2, color: '#e8dcae', speed: 45, life: 0.3, size: 2 })
  }
})

// ===========================================================================
// THE TWICE-CROWNED
// ===========================================================================

type Stage = 'duel' | 'hinge' | 'fallen'
type Combo = 'idle' | 'wind' | 'thrust' | 'gap' | 'recover'
type Blink = 'idle' | 'flourish' | 'dash'

class TwiceCrowned extends Boss {
  readonly id = D.id
  /** read structurally by game/hud.ts for the boss bar */
  readonly name = D.name
  /** The hinge is not a health threshold, so the bar carries no phase ticks. */
  readonly phaseTicks: readonly number[] = []

  private built = false
  private stage: Stage = 'duel'
  private enraged = false
  private t = 0

  private readonly throne: Vec = tileCenter(D.throneTile)

  // --- the hinge
  private crown: Enemy | null = null
  private crownT = 0
  private throneGlow = 0

  // --- the rapier combo
  private combo: Combo = 'idle'
  private comboT = 0
  private comboW = 0
  private thrustsLeft = 0
  private stepT = 0
  private gapT = 0
  private recoverT = 0
  private lx = 1
  private ly = 0
  private glintT = 0

  // --- the cape blink
  private blink: Blink = 'idle'
  private blinkT = 0
  private blinkW = 0
  private blinkLeft = 0
  private blinkVx = 0
  private blinkVy = 0
  private invuln = false
  private capeT = 0
  /** P2's afterimage: the ground he left behind burns for a moment. */
  private afterX = 0
  private afterY = 0
  private afterT = 0
  private afterTick = 0

  private summonT = 0

  private pingT = 0
  private hurtT = 0
  private dying = false
  private tookDamage = false
  private lastHp = -1

  constructor() {
    super(D.hp)
    this.radius = D.radius
    const c = tileCenter(D.startTile)
    this.pos.x = c.x
    this.pos.y = c.y
  }

  // ------------------------------------------------------------ small tools

  private get ph(): number {
    return this.phase
  }
  /** Enraged tightens every telegraph by a tenth of a second — never removes it. */
  private tele(v: number): number {
    return Math.max(0.3, v - (this.enraged ? D.enrage.telegraphCut : 0))
  }
  private ival(v: number): number {
    return v * (this.enraged ? D.enrage.intervalMul : 1)
  }

  /** Axis-separated move with a leading-edge wall check: the hall is open. */
  private tryMove(ctx: RunCtx, dx: number, dy: number): void {
    const r = this.radius * 0.7
    if (dx !== 0) {
      const nx = this.pos.x + dx
      if (!ctx.isWall(Math.floor((nx + Math.sign(dx) * r) / TILE), Math.floor(this.pos.y / TILE))) {
        this.pos.x = nx
      }
    }
    if (dy !== 0) {
      const ny = this.pos.y + dy
      if (!ctx.isWall(Math.floor(this.pos.x / TILE), Math.floor((ny + Math.sign(dy) * r) / TILE))) {
        this.pos.y = ny
      }
    }
  }

  /** Counts the re-formed twins too, so the hall cannot fill up sideways. */
  private liveCourtiers(ctx: RunCtx): number {
    let n = 0
    const list = ctx.enemies
    for (let i = 0; i < list.length; i++) {
      if (list[i].alive && list[i].def.id.startsWith(D.summon.defId)) n++
    }
    return n
  }

  // ------------------------------------------------------------------ build

  private build(ctx: RunCtx): void {
    this.built = true
    this.comboT = D.combo.interval[0] * 0.7
    this.blinkT = D.blink.interval[0]
    this.summonT = D.summon.interval[0] * 0.45
    this.lastHp = ctx.player.hp
    ctx.bark('THE TWICE-CROWNED. He has done this before.')
    ctx.sfx('door')
    ctx.shake(6, 0.7)
  }

  // ----------------------------------------------------------------- damage

  takeDamage(n: number): void {
    if (!this.alive || n <= 0) return
    // Collapsed, or mid-blink: the blow lands on nothing and says so.
    if (this.stage === 'hinge' || this.invuln) {
      if (this.pingT <= 0) {
        this.ctx?.sfx('ui')
        emit(this.pos.x, this.pos.y, {
          count: 5,
          color: this.invuln ? '#c9b6ef' : '#9aa6c4',
          speed: 130,
          life: 0.22,
          size: 3,
          shape: 1,
        })
      }
      this.pingT = 0.16
      return
    }
    const before = this.hp
    super.takeDamage(n)
    if (this.hp < before) this.hurtT = 0.12
  }

  // ----------------------------------------------------------------- update

  update(ctx: RunCtx, dt: number): void {
    this.tick(ctx, dt)
    if (!this.built) this.build(ctx)
    this.t += dt
    if (this.pingT > 0) this.pingT -= dt
    if (this.hurtT > 0) this.hurtT -= dt
    if (this.capeT > 0) this.capeT -= dt
    if (this.glintT > 0) this.glintT -= dt
    if (this.throneGlow > 0) this.throneGlow -= dt

    if (this.dying) return

    // no-hit watch: any drop in the Warden's health, from any source, spends the
    // clean kill. Under ?invuln=1 damagePlayer never lands, so an invulnerable
    // run reads as untouched — true, and noted where the test asserts it.
    if (this.lastHp >= 0 && ctx.player.hp < this.lastHp) this.tookDamage = true
    this.lastHp = ctx.player.hp

    this.updateAfterimage(ctx, dt)

    if (this.stage === 'hinge') {
      this.updateHinge(ctx, dt)
      return
    }

    if (this.hp <= 0) {
      if (this.phase === 1) this.beginHinge(ctx)
      else this.die(ctx)
      return
    }

    this.updateMove(ctx, dt)
    this.updateCombo(ctx, dt)
    this.updateBlink(ctx, dt)
    this.updateSummon(ctx, dt)
  }

  // ------------------------------------------------------------------ duel

  /** Poise: he holds a fencing band and circles it. He never bullrushes. */
  private updateMove(ctx: RunCtx, dt: number): void {
    if (this.combo === 'thrust' || this.combo === 'wind' || this.blink === 'dash') return
    const dx = ctx.player.pos.x - this.pos.x
    const dy = ctx.player.pos.y - this.pos.y
    const d = Math.hypot(dx, dy) || 1
    let mx = 0
    let my = 0
    if (d > D.walk.bandMax) {
      mx = dx / d
      my = dy / d
    } else if (d < D.walk.bandMin) {
      mx = -(dx / d)
      my = -(dy / d)
    }
    // always a little lateral drift, so he reads as circling rather than sliding
    const s = Math.sin(this.t * 0.9) * D.walk.strafe
    mx += (-dy / d) * s
    my += (dx / d) * s
    const sp = D.walk.speed * dt
    this.tryMove(ctx, mx * sp, my * sp)
  }

  private lockStep(ctx: RunCtx): void {
    const dx = ctx.player.pos.x - this.pos.x
    const dy = ctx.player.pos.y - this.pos.y
    const l = Math.hypot(dx, dy) || 1
    this.lx = dx / l
    this.ly = dy / l
    this.stepT = D.combo.stepTime
    this.glintT = 0.16
    emit(this.pos.x + this.lx * 26, this.pos.y + this.ly * 26, {
      count: 6,
      color: '#ff4a4a',
      speed: 160,
      life: 0.2,
      size: 3,
      angle: Math.atan2(this.ly, this.lx),
      spread: 1.1,
      shape: 1,
    })
  }

  private thrust(ctx: RunCtx): void {
    const dir = Math.atan2(this.ly, this.lx)
    const pdx = ctx.player.pos.x - this.pos.x
    const pdy = ctx.player.pos.y - this.pos.y
    const pd = Math.hypot(pdx, pdy)
    let inArc = pd <= D.combo.range + this.radius
    if (inArc && pd > 2) {
      let a = Math.atan2(pdy, pdx) - dir
      while (a > Math.PI) a -= TAU
      while (a < -Math.PI) a += TAU
      inArc = Math.abs(a) <= D.combo.arc / 2
    }
    if (inArc) ctx.damagePlayer(D.combo.damage, D.id)
    ctx.sfx('swing')
    emit(this.pos.x + this.lx * 30, this.pos.y + this.ly * 30, {
      count: 8,
      color: '#f0e4c8',
      speed: 200,
      life: 0.18,
      size: 3,
      angle: dir,
      spread: D.combo.arc,
      shape: 1,
    })
  }

  private updateCombo(ctx: RunCtx, dt: number): void {
    if (this.combo === 'idle') {
      if (this.blink !== 'idle') return
      this.comboT -= dt
      if (this.comboT > 0) return
      const dx = ctx.player.pos.x - this.pos.x
      const dy = ctx.player.pos.y - this.pos.y
      if (Math.hypot(dx, dy) > D.walk.bandMax * 1.6) return // close first, then strike
      this.combo = 'wind'
      this.comboW = this.tele(byPhase(D.combo.windup, this.ph))
      this.thrustsLeft = byPhase(D.combo.thrusts, this.ph)
      this.glintT = this.comboW
      this.telegraph('thrust', this.comboW)
      return
    }

    if (this.combo === 'wind') {
      this.comboW -= dt
      const dx = ctx.player.pos.x - this.pos.x
      const dy = ctx.player.pos.y - this.pos.y
      const l = Math.hypot(dx, dy) || 1
      // settle onto the rear foot: the pose that sells the lunge
      this.tryMove(ctx, (-dx / l) * 26 * dt, (-dy / l) * 26 * dt)
      this.glintT = Math.max(this.glintT, this.comboW)
      if (this.comboW <= 0) {
        this.combo = 'thrust'
        this.lockStep(ctx)
      }
      return
    }

    if (this.combo === 'thrust') {
      this.stepT -= dt
      const sp = D.combo.dashSpeed * dt
      this.tryMove(ctx, this.lx * sp, this.ly * sp)
      if (this.stepT <= 0) {
        this.thrust(ctx)
        this.thrustsLeft--
        if (this.thrustsLeft > 0) {
          this.combo = 'gap'
          this.gapT = this.ival(byPhase(D.combo.stepGap, this.ph))
        } else {
          this.combo = 'recover'
          this.recoverT = D.combo.recover
        }
      }
      return
    }

    if (this.combo === 'gap') {
      this.gapT -= dt
      if (this.gapT <= 0) {
        this.combo = 'thrust'
        this.lockStep(ctx)
      }
      return
    }

    this.recoverT -= dt
    if (this.recoverT <= 0) {
      this.combo = 'idle'
      this.comboT = this.ival(byPhase(D.combo.interval, this.ph))
    }
  }

  /**
   * The cape blink: he dashes THROUGH your answer on a cadence you can learn.
   * Invulnerable only for the dash itself, always after a flourish you can see.
   * In P2 the ground he vacated keeps burning for a moment.
   */
  private updateBlink(ctx: RunCtx, dt: number): void {
    if (this.blink === 'idle') {
      if (this.combo !== 'idle') return
      this.blinkT -= dt
      if (this.blinkT > 0) return
      this.blink = 'flourish'
      this.blinkW = this.tele(byPhase(D.blink.flourish, this.ph))
      this.capeT = this.blinkW
      this.telegraph('cape', this.blinkW)
      return
    }

    if (this.blink === 'flourish') {
      this.blinkW -= dt
      this.capeT = Math.max(this.capeT, this.blinkW)
      if (this.blinkW > 0) return
      const dx = ctx.player.pos.x - this.pos.x
      const dy = ctx.player.pos.y - this.pos.y
      const l = Math.hypot(dx, dy) || 1
      this.blinkVx = (dx / l) * (D.blink.distance / D.blink.seconds)
      this.blinkVy = (dy / l) * (D.blink.distance / D.blink.seconds)
      this.blinkLeft = D.blink.seconds
      this.blink = 'dash'
      this.invuln = true
      if (this.phase >= 2) {
        this.afterX = this.pos.x
        this.afterY = this.pos.y
        this.afterT = D.blink.afterimage.seconds
        this.afterTick = 0
      }
      ctx.sfx('dash')
      emit(this.pos.x, this.pos.y, {
        count: 20,
        color: '#8f7fb8',
        speed: 190,
        speedVar: 120,
        life: 0.4,
        lifeVar: 0.2,
        size: 4,
        shape: 1,
      })
      return
    }

    this.blinkLeft -= dt
    this.tryMove(ctx, this.blinkVx * dt, this.blinkVy * dt)
    emit(this.pos.x, this.pos.y, { count: 2, color: '#c9b6ef', speed: 40, life: 0.25, size: 3, shape: 1 })
    if (this.blinkLeft <= 0) {
      this.blink = 'idle'
      this.invuln = false
      this.blinkT = this.ival(byPhase(D.blink.interval, this.ph))
    }
  }

  /** P2 only. Self-contained hazard: RunCtx has no ground-effect API. */
  private updateAfterimage(ctx: RunCtx, dt: number): void {
    if (this.afterT <= 0) return
    this.afterT -= dt
    this.afterTick -= dt
    if (this.afterTick <= 0) {
      this.afterTick = 0.25
      const dx = ctx.player.pos.x - this.afterX
      const dy = ctx.player.pos.y - this.afterY
      if (Math.hypot(dx, dy) <= D.blink.afterimage.radius) {
        ctx.damagePlayer(D.blink.afterimage.dps * 0.25, 'twicecrowned-afterimage')
      }
    }
    emit(this.afterX, this.afterY, { count: 1, color: '#b48cff', speed: 30, life: 0.35, size: 3, shape: 1 })
  }

  /** Courtier pairs. Their husks are the zone rule, mid-duel (DESIGN §5). */
  private updateSummon(ctx: RunCtx, dt: number): void {
    this.summonT -= dt
    if (this.summonT > 0) return
    this.summonT = this.ival(byPhase(D.summon.interval, this.ph))
    if (this.liveCourtiers(ctx) >= D.summon.liveCap) return
    const elite: EliteRider | null = this.phase >= 2 ? 'frenzied' : null
    for (let i = 0; i < D.summon.count; i++) {
      const a = (i / D.summon.count) * TAU + this.t
      posScratch.x = this.pos.x + Math.cos(a) * D.summon.spread
      posScratch.y = this.pos.y + Math.sin(a) * D.summon.spread
      if (ctx.isWall(Math.floor(posScratch.x / TILE), Math.floor(posScratch.y / TILE))) {
        posScratch.x = this.pos.x
        posScratch.y = this.pos.y + D.summon.spread * 0.5
      }
      ctx.spawnEnemy(D.summon.defId, posScratch, { elite })
    }
    ctx.sfx('generator-die')
    ctx.bark(this.phase >= 2 ? 'The court answers, and it is angry.' : 'The court attends.')
  }

  // ------------------------------------------------------------- THE HINGE

  private beginHinge(ctx: RunCtx): void {
    this.stage = 'hinge'
    this.alive = true // the body is still on the floor; the fight is not over
    this.hp = 0
    this.crownT = 0
    this.combo = 'idle'
    this.blink = 'idle'
    this.invuln = false
    ctx.hitstop(D.hinge.hitstop)
    ctx.shake(D.hinge.shake, D.hinge.shakeSeconds)
    ctx.sfx('death')
    ctx.sfx('telegraph')
    ctx.bark('The crown remembers the throne.')
    emit(this.pos.x, this.pos.y, {
      count: 34,
      color: '#e8dcae',
      speed: 250,
      speedVar: 170,
      life: 0.8,
      lifeVar: 0.35,
      size: 4,
      grav: 200,
      drag: 0.88,
    })

    posScratch.x = this.pos.x
    posScratch.y = this.pos.y - 30
    const c = ctx.spawnEnemy(D.crown.defId, posScratch, { elite: null })
    if (!c.alive) {
      // no crown, no race — fail safe toward the player's favour
      this.riseAt(ctx, D.crown.riseFraction, false)
      return
    }
    c.maxHp = D.crown.hp
    c.hp = D.crown.hp
    c.tags.tx = this.throne.x
    c.tags.ty = this.throne.y
    // DESIGN says "6s crawl". The distance from wherever the duel ended to the
    // throne is not fixed, so the SPEED is derived and the WINDOW is the promise:
    // always six seconds and sixty hit points, wherever he fell.
    const dist = Math.hypot(this.throne.x - posScratch.x, this.throne.y - posScratch.y)
    ;(c as EnemyEnt).speed = Math.max(18, dist / D.crown.crawlSeconds)
    this.crown = c
  }

  private updateHinge(ctx: RunCtx, dt: number): void {
    this.crownT += dt
    const c = this.crown
    if (!c || !c.alive) {
      // destroyed mid-crawl: the Gloom still drags him up, at 55% and unhurried
      this.riseAt(ctx, D.crown.riseFraction, false)
      return
    }
    const d = Math.hypot(c.pos.x - this.throne.x, c.pos.y - this.throne.y)
    this.throneGlow = Math.max(this.throneGlow, 0.3)
    if (d <= D.throneRadius || this.crownT >= D.crown.maxCrawlSeconds) {
      c.kill(true)
      this.riseAt(ctx, 1, true)
    }
  }

  private riseAt(ctx: RunCtx, fraction: number, enraged: boolean): void {
    this.crown = null
    this.stage = 'duel'
    this.alive = true
    this.hp = Math.max(1, Math.round(this.maxHp * fraction))
    this.enraged = enraged
    this.setPhase(2)

    this.combo = 'idle'
    this.blink = 'idle'
    this.invuln = false
    this.comboT = this.ival(D.combo.interval[1]) * 0.5
    this.blinkT = this.ival(D.blink.interval[1])
    this.summonT = this.ival(D.summon.interval[1]) * 0.6

    ctx.hitstop(0.14)
    ctx.shake(enraged ? 10 : 6, 0.7)
    ctx.sfx('door')
    ctx.bark(
      enraged
        ? 'Crowned again. Nothing you did to him took.'
        : 'The crown is dust. He gets up anyway — with less of him left.',
    )
    emit(this.pos.x, this.pos.y, {
      count: 30,
      color: enraged ? '#ff5a3c' : '#dce7ff',
      speed: 220,
      speedVar: 150,
      life: 0.7,
      lifeVar: 0.3,
      size: 4,
      shape: 1,
    })
  }

  // ------------------------------------------------------------------ death

  private die(ctx: RunCtx): void {
    this.dying = true
    this.stage = 'fallen'
    this.alive = false
    this.hp = 0
    // nothing it started may outlive it: a death mid-combo would freeze the
    // glint, the cape and the blink afterimages over the corpse
    this.combo = 'idle'
    this.blink = 'idle'
    this.glintT = 0
    this.capeT = 0
    this.afterT = 0
    ctx.hitstop(D.death.hitstop)
    ctx.shake(D.death.shake, D.death.shakeSeconds)
    ctx.sfx('generator-die')
    ctx.sfx('death')
    for (let i = 0; i < 3; i++) {
      emit(this.pos.x, this.pos.y, {
        count: 38,
        color: i === 0 ? '#e8e4d4' : i === 1 ? '#b48cff' : '#8a2a2a',
        speed: 250 + i * 90,
        speedVar: 190,
        life: 0.9,
        lifeVar: 0.5,
        size: 5,
        grav: 180,
        drag: 0.88,
      })
    }
    // The court goes with him, silently — a silent death leaves no husk, so the
    // arena is genuinely quiet for the payout.
    const list = ctx.enemies
    for (let i = list.length - 1; i >= 0; i--) {
      if (list[i].alive) list[i].kill(true)
    }

    ctx.featTick('boss3_kill')
    if (!this.tookDamage) {
      ctx.featTick('boss3_nohit')
      ctx.bark('No blood for the court.')
    }
    ctx.bark('The Twice-Crowned stays down.')
    bossDefeated(ctx)
  }

  // ----------------------------------------------------------------- render

  render(d: Draw): void {
    const ctx = d.ctx
    this.renderThrone(ctx)
    if (this.afterT > 0) this.renderAfterimage(ctx)
    this.renderKing(ctx)
    if (this.stage === 'hinge') this.renderCrawl(ctx)
    if (this.combo === 'wind' || this.glintT > 0) this.renderGlint(ctx)
    if (this.capeT > 0) this.renderCape(ctx)
    this.renderTelegraphs(d)
  }

  /**
   * The throne, upstage, always. It is drawn from the boss because the boss owns
   * what it means — but it is therefore drawn OVER anything standing directly in
   * front of it. The throne sits two tiles off the north wall and nothing the
   * fight spawns has business there, so the trade is a comment, not a bug.
   */
  private renderThrone(ctx: CanvasRenderingContext2D): void {
    const x = this.throne.x
    const y = this.throne.y
    const glow = this.stage === 'hinge' ? 0.35 + 0.3 * Math.sin(this.t * 8) : 0.12
    ctx.save()
    ctx.globalAlpha = 0.35
    ctx.fillStyle = '#000000'
    ctx.beginPath()
    ctx.ellipse(x, y + 26, 34, 11, 0, 0, TAU)
    ctx.fill()
    ctx.globalAlpha = glow * 0.8
    ctx.fillStyle = this.stage === 'hinge' ? '#ffcf6b' : '#b48cff'
    ctx.beginPath()
    ctx.ellipse(x, y + 18, 62 + glow * 22, 26 + glow * 10, 0, 0, TAU)
    ctx.fill()
    ctx.restore()
    // upstage and oversized: the throne has to loom, and at the hinge it is the
    // only thing in the room that matters
    drawSprite(ctx, 'z3_throne', x, y - 10, { scale: 1.5 })
  }

  private renderKing(ctx: CanvasRenderingContext2D): void {
    const down = this.stage === 'hinge'
    const id = this.phase >= 2 || down ? 'z3_king_broken' : 'z3_king'
    ctx.save()
    ctx.globalAlpha = 0.36
    ctx.fillStyle = '#000000'
    ctx.beginPath()
    ctx.ellipse(this.pos.x, this.pos.y + this.radius * 0.8, this.radius * 1.05, this.radius * 0.4, 0, 0, TAU)
    ctx.fill()
    ctx.restore()

    if (this.invuln) {
      // mid-blink: he is a smear of cape and nothing you can touch
      ctx.save()
      ctx.globalAlpha = 0.5
      drawSprite(ctx, id, this.pos.x - this.blinkVx * 0.05, this.pos.y - this.blinkVy * 0.05, { scale: 1.02 })
      ctx.restore()
      drawSprite(ctx, id, this.pos.x, this.pos.y, { alpha: 0.55 })
      return
    }

    if (down) {
      // collapsed: slumped, dimmed, and unmistakably not finished
      ctx.save()
      ctx.translate(this.pos.x, this.pos.y + 10)
      ctx.rotate(0.42)
      drawSprite(ctx, id, 0, 0, { alpha: 0.7, scale: 0.98 })
      ctx.restore()
      return
    }

    drawSprite(ctx, id, this.pos.x, this.pos.y, {
      flip: this.lx < 0,
      white: this.hurtT > 0 ? 0.85 : this.combo === 'wind' ? 0.25 : 0,
      scale: this.combo === 'thrust' ? 1.06 : 1,
    })
  }

  /** The heart-stopper: a crown on legs, a line to the throne, and a clock. */
  private renderCrawl(ctx: CanvasRenderingContext2D): void {
    const c = this.crown
    if (!c || !c.alive) return
    ctx.save()
    // a hot ring under it: the Crown is drawn in the body pass, so it can end up
    // under the corpse it just left. This is drawn from the boss, after the
    // bodies, and guarantees the eye finds it.
    const beat = 0.5 + 0.5 * Math.sin(this.t * 10)
    ctx.globalAlpha = 0.3 + 0.3 * beat
    ctx.fillStyle = '#ffcf6b'
    ctx.beginPath()
    ctx.ellipse(c.pos.x, c.pos.y + 6, 22 + beat * 5, 11 + beat * 3, 0, 0, TAU)
    ctx.fill()
    ctx.globalAlpha = 0.9
    ctx.strokeStyle = '#fff0c0'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.arc(c.pos.x, c.pos.y, 17 + beat * 2, 0, TAU)
    ctx.stroke()

    // the line it is running along
    ctx.globalAlpha = 0.4 + 0.2 * Math.sin(this.t * 9)
    ctx.strokeStyle = '#ffcf6b'
    ctx.lineWidth = 2
    ctx.setLineDash([6, 6])
    ctx.beginPath()
    ctx.moveTo(c.pos.x, c.pos.y)
    ctx.lineTo(this.throne.x, this.throne.y + 8)
    ctx.stroke()
    ctx.setLineDash([])

    // its own bar, because the boss bar is honestly empty right now
    const w = 64
    const x = c.pos.x - w / 2
    const y = c.pos.y - 30
    ctx.globalAlpha = 1
    ctx.fillStyle = '#00000099'
    ctx.fillRect(x - 1, y - 1, w + 2, 7)
    ctx.fillStyle = '#ffcf6b'
    ctx.fillRect(x, y, w * Math.max(0, c.hp / Math.max(1, c.maxHp)), 5)
    ctx.font = '700 10px ui-monospace, "SF Mono", Menlo, monospace'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = '#000000'
    ctx.fillText('THE CROWN', c.pos.x + 1, y - 8)
    ctx.fillStyle = '#ffe6a8'
    ctx.fillText('THE CROWN', c.pos.x, y - 9)
    ctx.restore()
  }

  private renderGlint(ctx: CanvasRenderingContext2D): void {
    const k = 0.45 + 0.55 * Math.abs(Math.sin(this.t * 28))
    const gx = this.pos.x + this.lx * 22
    const gy = this.pos.y + this.ly * 22 - 6
    ctx.save()
    ctx.globalAlpha = k
    ctx.fillStyle = '#ff4a4a'
    ctx.beginPath()
    ctx.moveTo(gx, gy - 7)
    ctx.lineTo(gx + 5, gy)
    ctx.lineTo(gx, gy + 7)
    ctx.lineTo(gx - 5, gy)
    ctx.closePath()
    ctx.fill()
    ctx.globalAlpha = 0.35 * k
    ctx.beginPath()
    ctx.arc(gx, gy, 14, 0, TAU)
    ctx.fill()
    ctx.restore()
  }

  private renderCape(ctx: CanvasRenderingContext2D): void {
    const k = 1 - Math.max(0, Math.min(1, this.capeT / 0.5))
    ctx.save()
    ctx.globalAlpha = 0.25 + 0.35 * k
    ctx.strokeStyle = '#c9b6ef'
    ctx.lineWidth = 3
    ctx.beginPath()
    ctx.arc(this.pos.x, this.pos.y, this.radius + 12 + k * 22, this.t * 6, this.t * 6 + Math.PI * 1.3)
    ctx.stroke()
    ctx.restore()
  }

  private renderAfterimage(ctx: CanvasRenderingContext2D): void {
    const k = Math.max(0, Math.min(1, this.afterT / D.blink.afterimage.seconds))
    ctx.save()
    ctx.globalAlpha = 0.16 + 0.2 * k
    ctx.fillStyle = '#b48cff'
    ctx.beginPath()
    ctx.arc(this.afterX, this.afterY, D.blink.afterimage.radius, 0, TAU)
    ctx.fill()
    ctx.globalAlpha = 0.5 * k
    drawSprite(ctx, 'z3_king_broken', this.afterX, this.afterY, { alpha: 0.4 * k })
    ctx.restore()
  }
}

// ===========================================================================
// sprites
// ===========================================================================

const RD = Math.round

function pnum(p: Record<string, number | string>, k: string, d: number): number {
  const v = p[k]
  return typeof v === 'number' ? v : d
}
function pstr(p: Record<string, number | string>, k: string, d: string): string {
  const v = p[k]
  return typeof v === 'string' ? v : d
}

/** The king, both forms. `broken` swaps the crown for a cracked, bare skull. */
registerPainter('z3_king', (a: PixelArt, p) => {
  const s = a.size
  const cx = s / 2
  const broken = pnum(p, 'broken', 0) > 0
  const cloth = broken ? '#332c46' : '#453a63'
  const bone = broken ? '#cfc9b6' : '#efe9d6'
  const gold = pstr(p, 'gold', '#e8dcae')
  const clothLo = shade(cloth, -0.4)
  const clothHi = shade(cloth, 0.26)
  const boneLo = shade(bone, -0.28)

  // the train of the cape, spread behind him — the biggest shape on screen
  a.ellipse(cx, s * 0.72, s * 0.42, s * 0.24, clothLo)
  a.ellipse(cx, s * 0.66, s * 0.34, s * 0.2, cloth)
  for (let i = -3; i <= 3; i++) {
    a.vline(RD(cx + i * s * 0.1), RD(s * 0.6), RD(s * 0.28), clothLo)
  }
  // legs
  a.rect(RD(cx - s * 0.08), RD(s * 0.74), 4, RD(s * 0.16), boneLo)
  a.rect(RD(cx + s * 0.03), RD(s * 0.74), 4, RD(s * 0.16), boneLo)
  // regalia: a long bone cuirass over a court coat
  a.rect(RD(cx - s * 0.15), RD(s * 0.54), RD(s * 0.3), RD(s * 0.22), cloth)
  a.ellipse(cx, s * 0.46, s * 0.19, s * 0.16, bone)
  a.ellipse(cx, s * 0.5, s * 0.12, s * 0.09, boneLo)
  if (broken) {
    // the cracks: he has been killed once already and it shows
    a.line(RD(cx - s * 0.1), RD(s * 0.38), RD(cx + s * 0.04), RD(s * 0.54), '#2a2333')
    a.line(RD(cx + s * 0.04), RD(s * 0.54), RD(cx - s * 0.02), RD(s * 0.62), '#2a2333')
  }
  // pauldrons
  a.ellipse(cx - s * 0.24, s * 0.44, s * 0.1, s * 0.08, clothHi)
  a.ellipse(cx + s * 0.24, s * 0.44, s * 0.1, s * 0.08, clothHi)
  // the high collar of the court
  a.ellipse(cx, s * 0.36, s * 0.27, s * 0.06, bone)
  a.ellipse(cx, s * 0.345, s * 0.2, s * 0.035, shade(bone, 0.2))
  // head
  a.ellipse(cx, s * 0.25, s * 0.12, s * 0.13, bone)
  a.rect(RD(cx - s * 0.09), RD(s * 0.24), RD(s * 0.18), 2, '#171320')
  a.set(RD(cx - s * 0.07), RD(s * 0.24), broken ? '#ff6a4a' : '#ff4a4a')
  a.set(RD(cx + s * 0.05), RD(s * 0.24), broken ? '#ff6a4a' : '#ff4a4a')
  if (broken) {
    a.line(RD(cx - s * 0.06), RD(s * 0.14), RD(cx + s * 0.02), RD(s * 0.22), boneLo)
  } else {
    // THE CROWN: five points, unmistakable, and the thing that runs away later
    a.rect(RD(cx - s * 0.13), RD(s * 0.13), RD(s * 0.26), 3, gold)
    for (let i = -2; i <= 2; i++) {
      const x = RD(cx + i * s * 0.06)
      a.vline(x, RD(s * 0.13) - (i % 2 === 0 ? 4 : 2), i % 2 === 0 ? 4 : 2, gold)
    }
    a.set(RD(cx), RD(s * 0.14), '#c79bff')
  }
  // the rapier, held long and out
  const wx = RD(cx + s * 0.33)
  a.vline(wx, RD(s * 0.26), RD(s * 0.46), '#d3d9e6')
  a.vline(wx + 1, RD(s * 0.3), RD(s * 0.4), shade('#d3d9e6', -0.28))
  a.set(wx, RD(s * 0.25), '#ff4a4a')
  a.hline(wx - 3, RD(s * 0.72), 7, gold)
})

registerPainter('z3_crown', (a: PixelArt, p) => {
  const s = a.size
  const cx = s / 2
  const gold = pstr(p, 'gold', '#e8dcae')
  const gem = pstr(p, 'gem', '#c79bff')
  const goldLo = shade(gold, -0.36)
  const goldHi = shade(gold, 0.3)

  // spider legs first, so the circlet sits on top of them
  for (let i = 0; i < 3; i++) {
    const x = RD(cx - s * 0.3 + i * s * 0.3)
    a.line(x, RD(s * 0.6), RD(x - s * 0.12), RD(s * 0.88), goldLo)
    a.line(x, RD(s * 0.6), RD(x + s * 0.12), RD(s * 0.88), goldLo)
    a.set(RD(x - s * 0.12), RD(s * 0.9), gold)
    a.set(RD(x + s * 0.12), RD(s * 0.9), gold)
  }
  // the band
  a.ellipse(cx, s * 0.54, s * 0.3, s * 0.12, goldLo)
  a.ellipse(cx, s * 0.5, s * 0.3, s * 0.11, gold)
  a.ellipse(cx, s * 0.47, s * 0.22, s * 0.06, goldHi)
  // five points
  for (let i = -2; i <= 2; i++) {
    const x = RD(cx + i * s * 0.13)
    const h = RD(s * (i % 2 === 0 ? 0.22 : 0.14))
    a.vline(x, RD(s * 0.44) - h, h, gold)
    a.set(x, RD(s * 0.44) - h - 1, goldHi)
  }
  // the gem, watching
  a.disc(cx, s * 0.5, s * 0.09, '#241a30')
  a.disc(cx, s * 0.5, s * 0.06, gem)
  a.set(RD(cx), RD(s * 0.49), '#ffffff')
})

registerPainter('z3_throne', (a: PixelArt, p, pal) => {
  const s = a.size
  const cx = s / 2
  const stone = shade(pal.wall, -0.1)
  const lo = shade(stone, -0.36)
  const hi = shade(stone, 0.24)
  const gold = pstr(p, 'gold', '#e8dcae')

  // dais
  a.ellipse(cx, s * 0.88, s * 0.44, s * 0.09, lo)
  a.rect(RD(cx - s * 0.34), RD(s * 0.76), RD(s * 0.68), RD(s * 0.12), stone)
  a.hline(RD(cx - s * 0.34), RD(s * 0.76), RD(s * 0.68), hi)
  // seat
  a.rect(RD(cx - s * 0.24), RD(s * 0.6), RD(s * 0.48), RD(s * 0.17), stone)
  a.hline(RD(cx - s * 0.24), RD(s * 0.6), RD(s * 0.48), hi)
  // tall bone-panelled back
  a.rect(RD(cx - s * 0.2), RD(s * 0.14), RD(s * 0.4), RD(s * 0.47), stone)
  a.rect(RD(cx - s * 0.14), RD(s * 0.2), RD(s * 0.28), RD(s * 0.36), lo)
  for (let i = -1; i <= 1; i++) {
    a.vline(RD(cx + i * s * 0.1), RD(s * 0.2), RD(s * 0.36), hi)
  }
  // arms
  a.rect(RD(cx - s * 0.3), RD(s * 0.52), RD(s * 0.1), RD(s * 0.1), stone)
  a.rect(RD(cx + s * 0.2), RD(s * 0.52), RD(s * 0.1), RD(s * 0.1), stone)
  // the empty crown-rest at the top — it has been waiting
  a.rect(RD(cx - s * 0.12), RD(s * 0.1), RD(s * 0.24), 3, gold)
  for (let i = -1; i <= 1; i++) a.vline(RD(cx + i * s * 0.1), RD(s * 0.06), 4, gold)
  a.set(RD(cx), RD(s * 0.11), '#c79bff')
})

// ---------------------------------------------------------------- registry

registerRecipe('z3_king', { key: 'z3_king', size: 56, params: { broken: 0 } })
registerRecipe('z3_king_broken', { key: 'z3_king', size: 56, params: { broken: 1 } })
registerRecipe('z3_throne', { key: 'z3_throne', size: 64 })

registerZoneContent({ enemies: ZONE3_BOSS_ENEMIES })
registerBossArena(D.id, ZONE3_BOSS_ARENA)
registerBoss(D.id, () => new TwiceCrowned())
